import React, { useState } from "react";
import Loader from "../../../components/Loader";
import CommonDataGrid from "../../../components/CommonDataGrid";
import { Toaster } from "react-hot-toast";
import { GridColDef } from "@mui/x-data-grid";
import { TextField, IconButton, Tooltip, Chip } from "@mui/material";
import {
    PlayArrow,
    Stop,
    RestartAlt,
    Edit,
} from "@mui/icons-material";
import useCronSettings from "../useCronSettings";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import EditCronSettings from "./EditCronSettings";

const CronSettings: React.FC = () => {
    const {
        cronData,
        loading,
        searchQuery,
        setSearchQuery,
        handleStart,
        handleStop,
        handleReset,
        fetchData,
    } = useCronSettings();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [actionType, setActionType] = useState<"start" | "stop" | "reset">(
        "start"
    );
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);

    const openModal = (
        id: string,
        action: "start" | "stop" | "reset"
    ) => {
        setSelectedId(id);
        setActionType(action);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedId(null);
    };

    const confirmAction = async () => {
        if (selectedId) {
            switch (actionType) {
                case "start":
                    await handleStart(selectedId);
                    break;
                case "stop":
                    await handleStop(selectedId);
                    break;
                case "reset":
                    await handleReset(selectedId);
                    break;
            }
            closeModal();
        }
    };

    const getModalMessage = () => {
        switch (actionType) {
            case "start":
                return "Are you sure you want to start this cron job?";
            case "stop":
                return "Are you sure you want to stop this cron job?";
            case "reset":
                return "Are you sure you want to reset this cron job? This action cannot be undone.";
        }
    };

    const handleEdit = (id: string) => {
        setEditingId(id);
    };

    const closeEditModal = () => {
        setEditingId(null);
    };

    const handleEditSuccess = () => {
        fetchData(searchQuery);
    };

    const columns: GridColDef[] = [
        {
            field: "subject_id",
            headerName: "Subject Name",
            align: "center",
            headerAlign: "center",
            flex: 2,
            valueGetter: (value: any, row: any) => {
                const subject = row?.subject_id;
                if (!subject) return "";
                return `${subject.subject}`;
            },
        },
        {
            field: "end",
            headerName: "Total Questions",
            align: "center",
            headerAlign: "center",
            flex: 1,
        },
        {
            field: "time",
            headerName: "Time",
            align: "center",
            headerAlign: "center",
            flex: 1,
            valueGetter: (value: any) => value || "Not Set",
        },
        {
            field: "cron_status",
            headerName: "Status",
            align: "center",
            headerAlign: "center",
            flex: 1,
            renderCell: (params) => {
                const status = params.row.cron_status || "NotYet";

                const getStatusColor = (status: string) => {
                    switch (status) {
                        case "Running":
                            return "success";
                        case "Stopped":
                            return "warning";
                        case "NotYet":
                            return "default";
                        default:
                            return "default";
                    }
                };

                return (
                    <Chip
                        label={status}
                        color={getStatusColor(status) as any}
                        size="small"
                    />
                );
            },
        },
        {
            field: "progress",
            headerName: "Progress",
            align: "center",
            headerAlign: "center",
            flex: 1.5,
            valueGetter: (value: any, row: any) => {
                const sent = row.totalQuestionsSent || 0;
                const total = row.totalQuestions || 0;
                return `${sent} sent / ${total} total`;
            },
        },
        {
            field: "actions",
            headerName: "Actions",
            align: "center",
            headerAlign: "center",
            flex: 1.5,
            sortable: false,
            renderCell: (params) => {
                const status = params.row.cron_status;

                return (
                    <div className="flex justify-center items-center gap-2 h-full">
                        <Tooltip title="Start">
                            <span>
                                <IconButton
                                    color="success"
                                    onClick={() => openModal(params.row._id, "start")}
                                    disabled={status === "Running"}
                                    size="small"
                                >
                                    <PlayArrow />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Stop">
                            <span>
                                <IconButton
                                    color="warning"
                                    onClick={() => openModal(params.row._id, "stop")}
                                    disabled={status !== "Running"}
                                    size="small"
                                >
                                    <Stop />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Reset">
                            <span>
                                <IconButton
                                    color="error"
                                    onClick={() => openModal(params.row._id, "reset")}
                                    disabled={status === "NotYet"}
                                    size="small"
                                >
                                    <RestartAlt />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Edit">
                            <IconButton
                                color="primary"
                                onClick={() => handleEdit(params.row._id)}
                                size="small"
                            >
                                <Edit />
                            </IconButton>
                        </Tooltip>
                    </div>
                );
            },
        },
    ];

    return (
        <div className="mx-auto p-4">
            {loading && <Loader />}
            <div className="space-y-4 mt-4 bg-white p-4 rounded-b-lg shadow-md">
                <div className="flex justify-between items-center gap-4 mr-1 mb-4">
                    <TextField
                        fullWidth
                        variant="outlined"
                        label="Search"
                        placeholder="Type to search cron settings..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        size="small"
                        sx={{ maxWidth: "400px" }}
                    />
                </div>
                <CommonDataGrid
                    id="cronSettings"
                    rows={cronData}
                    columns={columns}
                    loading={!cronData}
                    showConfirmation={false}
                    confirmationMessage={""}
                    onCancelDelete={function (): void {
                        throw new Error("Function not implemented.");
                    }}
                    onConfirmDelete={function (): void {
                        throw new Error("Function not implemented.");
                    }}
                    toolbarProps={{ toolbar: { showQuickFilter: true } }}
                />
            </div>

            <ConfirmationModal
                isOpen={isModalOpen}
                onClose={closeModal}
                onConfirm={confirmAction}
                message={getModalMessage()}
            />

            {editingId && (
                <EditCronSettings
                    cronId={editingId}
                    onClose={closeEditModal}
                    onSuccess={handleEditSuccess}
                />
            )}

            <Toaster />
        </div>
    );
};

export default CronSettings;
